import classNames from 'classnames';
import { motion } from 'framer-motion';
import { useI18n } from '@/libraries/contexts/i18n-context';
import { useMemo, useState } from 'react';
import Icon from '@/components/atoms/icon/icon';
import Typography from '@/components/atoms/typography/typography';
import Checkbox, {
  CheckboxProps,
} from '@/components/molecules/checkbox/checkbox';
import DualChoice, {
  DualChoiceProps,
} from '@/components/molecules/dual-choice/dual-choice';
import Pill from '@/components/molecules/pill/pill';
import Radiobutton, {
  RadiobuttonProps,
} from '@/components/molecules/radiobutton/radiobutton';
import RangeSlider, {
  RangeSliderProps,
} from '@/components/molecules/range-slider/range-slider';
import FilterSlideInSingleCheckbox from '../filter-slide-in-single-checkbox/filter-slide-in-single-checkbox';
import styles from './filter-slide-in-input-group.module.scss';

export enum FilterInput {
  CHECKBOX = 'checkbox',
  DUAL_CHOICE = 'dual_choice',
  RADIO = 'radio',
  SINGLE_CHECKBOX = 'single_checkbox',
  SLIDER = 'slider',
}

export interface DualChoiceSwitch {
  /** The props of the dual choice */
  dualChoiceProps: DualChoiceProps;
  /** The label of the filter */
  label: string;
  /** The type of the filter */
  type: FilterInput.DUAL_CHOICE;
}

interface InputGroupBase {
  /** Facility identifier */
  facilityId?: string;
  /** Identifier */
  id: string;
  /** The label of the filter */
  label: DualChoiceSwitch | string;
  /** Name of the filter */
  name?: string;
}

export interface CheckBox extends InputGroupBase {
  /** The options to show */
  options: CheckboxProps[];
  /** The type of filter */
  type: FilterInput.CHECKBOX;
}

export interface Radio extends InputGroupBase {
  /** The options to show */
  options: RadiobuttonProps[];
  /** The type of filter */
  type: FilterInput.RADIO;
}

export interface SingleCheckBox extends InputGroupBase {
  /** The icon to render */
  icon?: IconName;
  /** Whether the checkbox is highlighted */
  isHighlighted?: boolean;
  /** The options to show */
  options: CheckboxProps[];
  /** The type of filter */
  type: FilterInput.SINGLE_CHECKBOX;
}

export interface Slider extends InputGroupBase {
  /** The options to show */
  options: RangeSliderProps[];
  /** The type of filter */
  type: FilterInput.SLIDER;
}

export type Filter = CheckBox | Radio | Slider | SingleCheckBox;

interface FilterSlideInInputGroupProps {
  /** The filter to show */
  filter: Filter;
  /** Whether the component is rendered in mobile view */
  isTablet?: boolean;
  /** The number of items to show before collapsing */
  overflowSize?: number;
}

const FilterSlideInInputGroup = ({
  filter,
  isTablet = false,
  overflowSize = 5,
}: FilterSlideInInputGroupProps) => {
  const { label, options, type: filterType, id } = filter;
  const icon =
    filterType === FilterInput.SINGLE_CHECKBOX
      ? (filter as SingleCheckBox).icon
      : undefined;
  const isHighlighted =
    filterType === FilterInput.SINGLE_CHECKBOX
      ? (filter as SingleCheckBox).isHighlighted
      : undefined;
  const { t } = useI18n();
  const [isCollapsed, setIsCollapsed] = useState(false);

  const calculatePillCount = useMemo(() => {
    let count = 0;

    if (filterType === FilterInput.SLIDER) {
      const sliderOption = options[0];

      if (sliderOption.value !== sliderOption.maxValue) {
        count = 1;
      }
    } else if (filterType === FilterInput.CHECKBOX) {
      count = (options as CheckboxProps[]).filter(
        option => option.checked,
      ).length;
    } else if (filterType === FilterInput.RADIO) {
      count = (options as CheckboxProps[]).filter(
        option => option.checked && option.id !== null,
      ).length;
    }

    return count;
  }, [filterType, options]);

  const motionProps = isTablet
    ? {}
    : {
        exit: { height: 0 },
        initial: { height: 0 },
      };

  const inputsRender = options.map((option, optionIndex) => {
    const ElementRender = () => {
      if (filterType === FilterInput.CHECKBOX) {
        return <Checkbox {...(option as CheckboxProps)} />;
      }

      if (filterType === FilterInput.RADIO) {
        return <Radiobutton {...(option as RadiobuttonProps)} />;
      }

      if (filterType === FilterInput.SLIDER) {
        return <RangeSlider {...(option as RangeSliderProps)} />;
      }

      if (filterType === FilterInput.SINGLE_CHECKBOX && !isHighlighted) {
        return <FilterSlideInSingleCheckbox option={options[0]} />;
      }
    };

    return <ElementRender key={optionIndex} />;
  });

  const hasOverflowItems = inputsRender.slice(overflowSize).length > 0;

  const showOverflowItems = isCollapsed && hasOverflowItems;

  const renderLabel = useMemo(() => {
    const isDualChoice = typeof label === 'object';

    const { label: labelText, dualChoiceProps } = isDualChoice
      ? label
      : { dualChoiceProps: undefined, label };

    return (
      <div className={styles.label}>
        <button
          className={styles.labelText}
          onClick={() =>
            !isTablet ? setIsCollapsed(previous => !previous) : undefined
          }
        >
          <Typography tagStyle="bodyMediumBold">{labelText}</Typography>

          {!isTablet && calculatePillCount !== 0 && (
            <Pill count={calculatePillCount} variant="secondary" />
          )}
        </button>

        {isDualChoice && <DualChoice {...dualChoiceProps} type="button" />}

        <button
          className={styles.collapseMobile}
          onClick={() => setIsCollapsed(previous => !previous)}
        >
          <Icon name={isCollapsed ? 'ChevronUp' : 'ChevronDown'} />
        </button>
      </div>
    );
  }, [label, isTablet, calculatePillCount, isCollapsed]);

  return (
    <div
      className={classNames(styles.component, {
        [styles.isMobile]: !isTablet,
      })}
      id={id}
    >
      {!isHighlighted && renderLabel}

      {filterType === FilterInput.SINGLE_CHECKBOX && isHighlighted && (
        <FilterSlideInSingleCheckbox
          icon={icon}
          isHighlighted={isHighlighted}
          option={options[0]}
        />
      )}

      <motion.div
        key={isTablet ? 'large-screen' : 'small-screen'}
        animate={{ height: isTablet || isCollapsed ? 'auto' : 0 }}
        className={classNames(styles.inputContainer, styles.inputs, {
          [styles.isCollapsed]: isCollapsed,
        })}
        {...motionProps}
        transition={{ duration: 0.25 }}
      >
        {inputsRender.slice(0, overflowSize)}

        <motion.div
          animate={{ height: showOverflowItems ? 'auto' : 0 }}
          className={classNames(styles.inputs, {
            [styles.hideOverflowItems]: !hasOverflowItems,
            [styles.isCollapsed]: isCollapsed,
            [styles.showOverflowItems]: isTablet && showOverflowItems,
          })}
          style={{ height: 0 }}
        >
          {inputsRender.slice(overflowSize)}
        </motion.div>
      </motion.div>

      {inputsRender.length > overflowSize && (
        <button
          className={styles.collapse}
          onClick={() => setIsCollapsed(previous => !previous)}
        >
          <Typography tagStyle="bodyMedium">
            {isCollapsed
              ? t('items.search.filterSlideIn.showLess')
              : t('items.search.filterSlideIn.showMore', {
                  number: inputsRender.length - overflowSize,
                })}
          </Typography>
        </button>
      )}
    </div>
  );
};

FilterSlideInInputGroup.displayName = 'FilterSlideInInputGroup';

export default FilterSlideInInputGroup;
